import { useCallback, useEffect, useState } from 'react';
import * as LaunchDarkly from 'launchdarkly-js-client-sdk';

import CONFIG from 'jsx/constants/config';
import COOKIE_KEYS from 'jsx/constants/cookieKeys';
import type { Flag } from 'jsx/constants/featureFlagsAndMetrics';
import { LD_FLAGS } from 'jsx/constants/featureFlagsAndMetrics';
import cookie from 'jsx/utils/cookie';
import { getSingletonUuid } from 'jsx/utils/uuid';
import USER_AGENT from 'jsx/utils/userAgent';

import { getQueryParameterValue } from './queryParameters';
import PatientModel from '../models/patient';
import { Brand } from './brand';

// Notion doc: https://www.notion.so/curology/LaunchDarkly-Codebase-Usage-951fa6eb639d4ceca0a3cfa346d1179c

let ldClient: LaunchDarkly.LDClient;
let ldClientReady = false;
const onReadyCallbacks: { (): void }[] = [];

const getLdUuidAndSetLdUuidCookie = (patient?: PatientModel) => {
  const ldUuidCookieSet = cookie.has(COOKIE_KEYS.ldUuid);
  let ldUuid;

  if (patient?.launchDarklyUuid) {
    ldUuid = patient.launchDarklyUuid;
  } else if (ldUuidCookieSet) {
    ldUuid = cookie.get(COOKIE_KEYS.ldUuid);
  } else if (cookie.has(COOKIE_KEYS.curologyUuid)) {
    ldUuid = cookie.get(COOKIE_KEYS.curologyUuid);
  } else {
    ldUuid = getSingletonUuid();
  }

  if (ldUuid) {
    cookie.set(COOKIE_KEYS.ldUuid, ldUuid, { expires: 395 });
  }

  return ldUuid;
};

export const getContext = (patient?: PatientModel) => {
  const currentCookieValue = cookie.has(COOKIE_KEYS.ldUuid)
    ? cookie.get(COOKIE_KEYS.ldUuid)
    : null;
  const ldUuid = getLdUuidAndSetLdUuidCookie(patient);
  const { displayName } = new Brand(CONFIG.appBrand);

  if (currentCookieValue && ldUuid && currentCookieValue !== ldUuid) {
    return {
      brand: displayName,
      kind: 'multi',
      user: {
        key: ldUuid,
        deviceData: USER_AGENT.USER_AGENT_PARSED.ua,
      },
      secondaryUser: {
        key: currentCookieValue,
        deviceData: USER_AGENT.USER_AGENT_PARSED.ua,
      },
    };
  }

  return {
    kind: 'user',
    key: ldUuid,
    userId: patient?.id,
    brand: displayName,
    deviceData: USER_AGENT.USER_AGENT_PARSED.ua,
  };
};

export const initLd = (patient?: PatientModel) => {
  const clientSideId = window.CUROLOGY_ENV.LD_CLIENT_SIDE_ID;

  if (!clientSideId) {
    return;
  }
  const context = getContext(patient);

  ldClient = LaunchDarkly.initialize(clientSideId, context, {
    sendEventsOnlyForVariation: true,
  });

  ldClient.on('ready', () => {
    ldClientReady = true;

    onReadyCallbacks.forEach((onReadyCallback) => {
      onReadyCallback();
    });
  });
};

/**
 * There are two possible ways to override a LaunchDarkly flag value in a development environment.
 *
 *  1. Add a query parameter to your request with the flag name and override value. Example: for
 *      a flag named login, add `?login=true`
 *  2. Set the `value` of the LaunchDarkly flag to anything other than undefined in `jsx/constants/featureFlagsAndMetrics`
 *
 *  Note that a query param will override a value specified in `jsx/constants/featureFlagsAndMetrics`
 */
const getFlagOverride = <T>(flagName: Flag): T | undefined => {
  if (CONFIG.isProduction) {
    return undefined;
  }

  return (getQueryParameterValue(flagName) ??
    LD_FLAGS[flagName]) as unknown as T;
};

export const useFlag = <T>(flagName: Flag) => {
  const [flagValue, setFlagValue] = useState<T>();

  const evaluate = useCallback(() => {
    const override = getFlagOverride<T>(flagName) as unknown as T | undefined;

    if (override !== undefined) {
      setFlagValue(override);
      return;
    }

    const variation = ldClient.variationDetail(flagName);

    if (variation.reason?.kind === 'ERROR') {
      // eslint-disable-next-line no-console
      console.warn(
        `Error evaluating flag ${flagName}: ${variation.reason.errorKind}`,
      );
    }

    // Circular dependency with `trackEvent`, so we can just do the underlying implementation here.
    window.dataLayer.push({ event: 'Reset DataLayer', properties: null });
    window.dataLayer.push({
      event: 'Flag Evaluated',
      properties: {
        flag: flagName,
        variation: variation.value,
        reason: variation.reason,
      },
    });

    setFlagValue(variation.value as T);
  }, [flagName]);

  useEffect(() => {
    if (ldClientReady) {
      evaluate();
    } else {
      onReadyCallbacks.push(evaluate);
    }
  }, [evaluate, flagName]);

  return flagValue;
};

export const trackCustomFeatureFlagEvent = (eventName: string, data?: {}) => {
  if (ldClientReady) {
    ldClient.track(eventName, data);
  } else {
    onReadyCallbacks.push(() => {
      ldClient.track(eventName, data);
    });
  }
};

export const updateContext = (patient: PatientModel) => {
  const context = getContext(patient);
  if (ldClientReady) {
    ldClient.identify(context);
  } else {
    onReadyCallbacks.push(() => {
      ldClient.identify(context);
    });
  }
};
